"use client";

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, Plus, Trophy } from "lucide-react";
import { Button } from "@vionixasia/shared-components";
import { AdminShell } from "@/components/AdminShell";
import { StatCard } from "@/components/StatCard";
import { apiClient } from "@/lib/api-client";
import { useQueryParam } from "@/lib/use-query-param";
import type { ResultEvent } from "@/types/event";
import type { MessagingProject } from "@/types/project";

export default function ProjectPage() {
  const projectId = useQueryParam("projectId");
  const [project, setProject] = useState<MessagingProject | null>(null);
  const [events, setEvents] = useState<ResultEvent[]>([]);

  useEffect(() => {
    if (!projectId) return;
    apiClient<{ project: MessagingProject }>(`/api/projects/${projectId}`).then((data) => setProject(data.project));
    apiClient<{ events: ResultEvent[] }>(`/api/projects/${projectId}/events`).then((data) => setEvents(data.events)).catch(() => setEvents([]));
  }, [projectId]);

  const stats = useMemo(() => ({
    events: events.length,
    results: events.reduce((sum, event) => sum + (event.total_results || 0), 0),
    sent: events.reduce((sum, event) => sum + (event.total_whatsapp_sent || 0), 0),
    failed: events.reduce((sum, event) => sum + (event.total_whatsapp_failed || 0), 0),
  }), [events]);

  return (
    <AdminShell>
      {!projectId || !project ? (
        <p className="text-sm text-muted">Loading project...</p>
      ) : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold text-ink">{project.name}</h1>
              <p className="text-sm text-muted">{project.slug} · {project.type} · {project.status}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button as={Link} variant="muted" to={`/dashboard/inbox?projectId=${project.id}`}>
                <Inbox size={16} /> Inbox
              </Button>
              <Button as={Link} color="teal" to={`/dashboard/event/new?projectId=${project.id}`}>
                <Plus size={16} /> Create Event
              </Button>
            </div>
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Events" value={stats.events} />
            <StatCard label="Results" value={stats.results} />
            <StatCard label="WhatsApp Sent" value={stats.sent} />
            <StatCard label="Failed Message" value={stats.failed} />
          </div>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {events.map((event) => (
              <Link key={event.id} className="rounded-card border border-line bg-white p-5 hover:border-brand" to={`/dashboard/event?projectId=${project.id}&eventId=${event.id}`}>
                <Trophy className="text-brand" size={20} />
                <h2 className="mt-3 font-semibold text-ink">{event.name}</h2>
                <p className="mt-1 text-sm text-muted">{event.date} · {event.location || "No location"} · {event.status}</p>
                <p className="mt-4 text-sm text-muted">{event.total_results || 0} results · {event.total_whatsapp_sent || 0} sent</p>
              </Link>
            ))}
            {events.length === 0 && <div className="rounded-card border border-line bg-white p-6 text-sm text-muted">Belum ada event di project ini.</div>}
          </div>
        </>
      )}
    </AdminShell>
  );
}
